<script setup lang="ts">
/**
 * 购买套餐（支付页）。
 *
 * 页面骨架、未登录分支，以及"必须等会话恢复完成"的首屏加载写法都照
 * `views/QuotaView.vue`——那里记录了冷启动的事故：组件会在 `runBootstrap()`
 * 结束之前挂载，那一瞬间 `auth.hydrated` 还是 false，写成 `onMounted` 里判一次
 * 就再也没有人来启动加载器，页面永远停在空态且不重试。
 *
 * 三条本页特有的约束：
 * 1. **金额以服务端为准**。预览用 `quotePlanChange`（契约里的纯函数，后端算账用的是
 *    同一个），但待支付订单里显示的永远是 `order.amountFen`。两者不一致时用户看到的是后者。
 * 2. **订阅没拉到就不许下单**。没有当前订阅就分不清"开通"与"补差价升级"，
 *    金额可能整个方向都错；宁可让按钮停住并给一个重试入口。
 * 3. **幂等键跟着"下单意图"走**：改了档位/周期/渠道就换新键，网络失败则保留，
 *    于是"连点两下"和"重试一次"都只会得到同一笔订单。
 */

import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
  DEFAULT_PLAN_ID,
  ORDER_TTL_SECONDS,
  OrderResponseSchema,
  PaymentResultResponseSchema,
  QuotaResponseSchema,
  SubscriptionResponseSchema,
  effectivePlanId,
  formatPriceYuan,
  isBillingCycle,
  isPaidPlanId,
  planLabelOf,
  quotePlanChange,
} from '@spirit/contracts';
import type {
  BillingCycle,
  Order,
  OrderResponse,
  PaidPlanId,
  PaymentChannel,
  PaymentResultResponse,
  PlanChangeResult,
  Quota,
  QuotaResponse,
  Subscription,
  SubscriptionResponse,
} from '@spirit/contracts';

import AppFooter from '../components/layout/AppFooter.vue';
import Button from '../components/ui/Button.vue';
import Modal from '../components/ui/Modal.vue';
import SkeletonText from '../components/ui/SkeletonText.vue';
import { pushToast } from '../components/ui/toast-store';
import { apiRequest, isApiError } from '../lib/api-client';
import { emitEvent } from '../lib/api-events';
import { applyQuota, useAuth } from '../lib/auth-store';
import { applySubscription } from '../lib/subscription-store';
import {
  CHANNEL_OPTIONS,
  CYCLE_OPTIONS,
  DEFAULT_CHECKOUT_PLAN,
  describePlanChange,
  describePlanChangeRefusal,
  newIdempotencyKey,
  payableOrderOf,
  paymentCountdown,
  planCardsOf,
} from '../lib/checkout';
import { IS_DEV } from '../lib/env';
import { formatDateTime } from '../lib/format';

const auth = useAuth();
const route = useRoute();

/** 查询参数可能是数组（`?plan=a&plan=b`），只认单个字符串形式，非法值静默回退。 */
function queryValue(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

const planParam = queryValue(route.query['plan']);
const cycleParam = queryValue(route.query['cycle']);

const selectedPlanId = ref<PaidPlanId>(
  planParam !== null && isPaidPlanId(planParam) ? planParam : DEFAULT_CHECKOUT_PLAN,
);
const cycle = ref<BillingCycle>(cycleParam !== null && isBillingCycle(cycleParam) ? cycleParam : 'monthly');
const channel = ref<PaymentChannel>('wechat');

const cards = computed(() => planCardsOf(cycle.value));
const cycleUnit = computed(() => (cycle.value === 'monthly' ? '月' : '年'));

const subscription = ref<Subscription | null>(null);
/** 订阅查询是否成功过（结果本身可以是 null）。false 时禁止下单：见文件头约束 2。 */
const subscriptionReady = ref(false);
const loading = ref(false);

const order = ref<Order | null>(null);
const orderPanelOpen = ref(false);

const creating = ref(false);
const checking = ref(false);
const closing = ref(false);
const mockPaying = ref(false);

/**
 * 倒计时的心跳。
 *
 * 用 `setInterval` 而不是 `requestAnimationFrame`：秒级文案 1 秒一跳足够，
 * 而 rAF 在后台标签页会被压到几乎不跑——"切到微信扫码再切回来"正是本页最常见的
 * 用法，那一刻倒计时必须还在走。
 */
const now = ref(Date.now());
const ticker = window.setInterval(() => {
  now.value = Date.now();
}, 1_000);
onBeforeUnmount(() => {
  window.clearInterval(ticker);
});

const needsLogin = computed(() => auth.hydrated && auth.status !== 'authenticated');

/**
 * 结算预览。
 *
 * `new Date(now.value)` 而不是 `new Date()`：让预览随时间一起走（倒计时在跑），
 * 同时保持"时间从哪里来"只有一个来源。
 */
const quote = computed<PlanChangeResult>(() =>
  quotePlanChange(subscription.value, selectedPlanId.value, cycle.value, new Date(now.value)),
);
const change = computed(() => (quote.value.ok ? describePlanChange(quote.value, cycle.value) : null));
const refusal = computed(() => (quote.value.ok ? null : describePlanChangeRefusal(quote.value, cycle.value)));

const currentPlanText = computed(() => {
  const effective = effectivePlanId(subscription.value, new Date(now.value));
  return effective === DEFAULT_PLAN_ID ? `${planLabelOf(DEFAULT_PLAN_ID)}（未开通）` : planLabelOf(effective);
});

const amountText = computed(() => (change.value === null ? '—' : `¥${formatPriceYuan(change.value.amountFen)}`));

const grantRangeText = computed(() => {
  const current = quote.value;
  if (!current.ok) return '—';
  return `${formatDateTime(current.quote.startsAt)} — ${formatDateTime(current.quote.expiresAt)}`;
});

const payLimitText = computed(() => `下单后 ${ORDER_TTL_SECONDS / 60} 分钟内有效`);
const payWindowText = computed(() => `${payLimitText.value}，超时订单自动关闭。`);

const canPay = computed(() => subscriptionReady.value && quote.value.ok);

const payButtonText = computed(() => {
  if (creating.value) return '正在创建订单…';
  if (!subscriptionReady.value) return '订阅信息未就绪';
  if (!quote.value.ok) return '当前不可购买';
  return `立即支付 ${amountText.value}`;
});

/**
 * 仍然可以付款的那笔订单（`paid` / `closed` / `failed` 都是 null）。
 *
 * 下面所有"待支付"相关的渲染与倒计时都必须经由它，而不是判 `order !== null`：
 * 支付成功后 `order` 里放的仍是刚拿到的那一笔（此时已是 `paid`），只判非空会让
 * 那张「有一笔待支付订单」的卡片连同它的 15 分钟倒计时一起复活。
 */
const pendingOrder = computed(() => payableOrderOf(order.value));

const countdown = computed(() =>
  pendingOrder.value === null ? null : paymentCountdown(pendingOrder.value.payExpiresAt, now.value),
);
const countdownText = computed(() => {
  const current = countdown.value;
  if (current === null) return '—';
  return current.expired ? '已超时' : `剩余 ${current.text}`;
});

const orderAmountText = computed(() =>
  pendingOrder.value === null ? '' : `应付 ¥${formatPriceYuan(pendingOrder.value.amountFen)}`,
);
const orderNoText = computed(() => pendingOrder.value?.orderNo ?? '');
const qrImageSrc = computed(() => pendingOrder.value?.qrImageDataUrl ?? null);

/**
 * 二维码的降级出口。
 *
 * Mock 与部分渠道不返回图片，只给跳转链接（微信给 `codeUrl`、支付宝给 `payUrl`）。
 * 没有图就退化成链接：用户仍然能把这笔订单付掉，而不是卡在一个空白方框前。
 */
const qrFallback = computed<{ href: string; label: string } | null>(() => {
  const current = pendingOrder.value;
  if (current === null) return null;
  const payUrl = current.payUrl ?? '';
  const codeUrl = current.codeUrl ?? '';
  if (current.channel === 'alipay' && payUrl.length > 0) return { href: payUrl, label: '在支付宝中打开' };
  if (codeUrl.length > 0) return { href: codeUrl, label: '打开支付链接' };
  if (payUrl.length > 0) return { href: payUrl, label: '打开支付链接' };
  return null;
});

const orderSummaryText = computed(() => {
  const current = pendingOrder.value;
  if (current === null) return '';
  return `${planLabelOf(current.planId)} · ${orderAmountText.value} · ${countdownText.value}`;
});

/** 幂等键：一次"下单意图"对应一个键。它不是响应式状态，改它不需要触发渲染。 */
let idempotencyKey: string | null = null;

/**
 * 倒计时归零只提示一次。
 *
 * `now` 每秒都在变，`countdown` 是 computed，归零之后每一秒都会重新求值；
 * 没有这个守卫就会每秒弹一个"订单已过期"。
 */
let expiredOrderNo: string | null = null;

function messageOf(caught: unknown, fallback: string): string {
  if (isApiError(caught) && caught.message.length > 0) {
    return caught.message;
  }
  return fallback;
}

async function loadSubscription(): Promise<void> {
  loading.value = true;
  try {
    const result = await apiRequest<SubscriptionResponse>('/subscription', SubscriptionResponseSchema, { method: 'GET' });
    subscription.value = result.subscription;
    subscriptionReady.value = true;
    // 同步给共享单例：顶栏的档位徽章读的是那一份，不写回去就会出现
    // "本页说已升到 Pro、右上角还写着 Lite"。
    applySubscription(result.subscription);
  } catch (caught) {
    // 失败时保持"未就绪"：金额算不准就不让下单（见文件头约束 2）。
    subscriptionReady.value = false;
    pushToast({
      tone: 'danger',
      title: '订阅信息加载失败',
      description: messageOf(caught, '请点击页面上的「重新加载」再试一次。'),
    });
  } finally {
    loading.value = false;
  }
}

async function refreshQuota(): Promise<void> {
  try {
    const result = await apiRequest<QuotaResponse>('/quota', QuotaResponseSchema, { method: 'GET' });
    applyQuota(result.quota);
  } catch {
    // 额度拉取失败不该把"支付成功"变成失败：下一次问答结算还会再刷新一次。
  }
}

/**
 * 支付成功的收尾。
 *
 * 两个入口拿到的载荷不一样：`/payments/mock/:orderNo` 一次带回
 * `{order, quota, subscription}`，而 `GET /orders/:orderNo` 只有 `{order}`。
 * 缺什么就地补拉一次，绝不让"已经付了钱"卡在数据没跟上。
 */
async function completePayment(quota: Quota | null, nextSubscription: Subscription | null): Promise<void> {
  const paid = order.value;
  if (paid === null) return;

  orderPanelOpen.value = false;
  if (quota !== null) {
    applyQuota(quota);
  } else {
    await refreshQuota();
  }
  if (nextSubscription !== null) {
    subscription.value = nextSubscription;
  } else {
    await loadSubscription();
  }
  // 顶栏徽章与到期横幅立刻跟上（`loadSubscription()` 内部已经写过一次，
  // 这里对"一次带回 subscription"的入口再保证一次）。
  applySubscription(subscription.value);

  // 额度条与其它页面听这个事件，而不是各自轮询。
  emitEvent('ui:quota-changed', { reason: 'manual' });
  // 这笔订单已经付掉了：把它从 `order` 里清掉，否则「有一笔待支付订单」那张卡片
  // 会带着已支付订单和它的倒计时重新出现（用户会以为钱没付成功）。
  order.value = null;
  pushToast({
    tone: 'success',
    title: '支付成功',
    description: `${planLabelOf(paid.planId)} 已开通，权益有效至 ${formatDateTime(paid.grantExpiresAt)}。`,
  });
}

async function startPayment(): Promise<void> {
  if (!canPay.value) return;
  creating.value = true;
  if (idempotencyKey === null) {
    idempotencyKey = newIdempotencyKey();
  }
  try {
    const result = await apiRequest<OrderResponse>('/orders', OrderResponseSchema, {
      method: 'POST',
      body: { planId: selectedPlanId.value, billingCycle: cycle.value, channel: channel.value },
      // 后端把"幂等键 → 订单"存 24 小时：连点两下、网络抖动后的重试，
      // 拿回的都是同一笔订单，不会重复扣款。
      headers: { 'Idempotency-Key': idempotencyKey },
    });
    order.value = result.order;
    orderPanelOpen.value = true;
    // 下单成功后立刻换新键：下一笔订单是新的意图。
    idempotencyKey = null;
    expiredOrderNo = null;
  } catch (caught) {
    // 失败**保留**键：如果只是超时/断网，用户再点一次仍是同一笔订单的重试。
    pushToast({ tone: 'danger', title: '下单失败', description: messageOf(caught, '请稍后重试。') });
  } finally {
    creating.value = false;
  }
}

async function checkPayment(): Promise<void> {
  const current = order.value;
  if (current === null) return;
  checking.value = true;
  try {
    const path = `/orders/${encodeURIComponent(current.orderNo)}`;
    const result = await apiRequest<OrderResponse>(path, OrderResponseSchema, { method: 'GET' });
    order.value = result.order;

    if (result.order.status === 'paid') {
      await completePayment(null, null);
      return;
    }
    if (result.order.status === 'closed' || result.order.status === 'failed') {
      // 契约 §3.5：`closed`（超时/主动关闭）与 `failed`（渠道明确拒付）都不可支付。
      // `failed` 目前没有生产者（没有任何仓储方法能写入它），但客户端必须按
      // "不可支付"处置——把只在测试里成立的分支写成界面分支，等于给未来埋雷。
      const failed = result.order.status === 'failed';
      order.value = null;
      orderPanelOpen.value = false;
      pushToast(
        failed
          ? { tone: 'warning', title: '订单支付失败', description: '本次支付未成功，没有产生费用，请重新下单。' }
          : { tone: 'warning', title: '订单已关闭', description: '请重新选择套餐下单。' },
      );
      return;
    }
    // 渠道回调通常有几秒延迟，这里刻意不自动重试：让用户自己决定再点一次。
    pushToast({
      tone: 'warning',
      title: '尚未收到支付结果',
      description: '支付完成后回调可能有几秒延迟，稍等片刻再点一次「我已完成支付」。',
    });
  } catch (caught) {
    pushToast({ tone: 'danger', title: '查询订单失败', description: messageOf(caught, '请稍后重试。') });
  } finally {
    checking.value = false;
  }
}

async function mockPay(): Promise<void> {
  const current = payableOrderOf(order.value);
  if (current === null) return;
  mockPaying.value = true;
  try {
    const path = `/payments/mock/${encodeURIComponent(current.orderNo)}`;
    const result = await apiRequest<PaymentResultResponse>(path, PaymentResultResponseSchema, { method: 'POST' });
    order.value = result.order;
    await completePayment(result.quota, result.subscription);
  } catch (caught) {
    pushToast({ tone: 'danger', title: '模拟支付失败', description: messageOf(caught, '请稍后重试。') });
  } finally {
    mockPaying.value = false;
  }
}

async function cancelOrder(): Promise<void> {
  const current = payableOrderOf(order.value);
  if (current === null) return;
  closing.value = true;
  try {
    const path = `/orders/${encodeURIComponent(current.orderNo)}/close`;
    const result = await apiRequest<OrderResponse>(path, OrderResponseSchema, { method: 'POST' });
    order.value = null;
    orderPanelOpen.value = false;
    idempotencyKey = null;
    pushToast({
      tone: 'info',
      title: '订单已取消',
      description: `${planLabelOf(result.order.planId)} 未支付，没有产生任何费用。`,
    });
  } catch (caught) {
    pushToast({ tone: 'danger', title: '取消订单失败', description: messageOf(caught, '请稍后重试。') });
  } finally {
    closing.value = false;
  }
}

// 改了档位/周期/渠道就是另一个"下单意图"。
watch([selectedPlanId, cycle, channel], () => {
  idempotencyKey = null;
});

watch(
  countdown,
  (value) => {
    const current = pendingOrder.value;
    if (current === null || value === null || !value.expired || expiredOrderNo === current.orderNo) return;
    expiredOrderNo = current.orderNo;
    order.value = null;
    orderPanelOpen.value = false;
    pushToast({
      tone: 'warning',
      title: '订单已过期',
      description: `${ORDER_TTL_SECONDS / 60} 分钟内未完成支付，请重新下单。`,
    });
  },
  { immediate: false },
);

/**
 * 首次加载订阅，**必须等会话恢复完成**。
 *
 * 冷启动（F5、直接打开 /checkout、从书签进入）时这个组件会在 `runBootstrap()`
 * 结束之前挂载，那一刻 `auth.hydrated` 还是 false。写成 `onMounted` 里判一次，
 * 挂载瞬间条件不成立就再没有人来启动加载器：页面永远停在空态且不重试，
 * 只有切走再切回来才会正常。watch 把它与"什么时候恢复完成"解耦。
 */
let started = false;
watch(
  () => auth.hydrated && auth.status === 'authenticated',
  (ready) => {
    if (!ready || started) return;
    started = true;
    void loadSubscription();
  },
  { immediate: true },
);
</script>

<template>
  <div v-if="needsLogin" class="mx-auto w-full max-w-2xl px-4 py-12 text-center">
    <h1 class="text-lg font-semibold text-slate-900">登录后购买套餐</h1>
    <p class="mt-2 text-sm text-slate-600">订单与权益都记在你的账号下，换设备也还在。</p>
    <RouterLink to="/login" class="mt-6 inline-block"><Button>去登录</Button></RouterLink>
  </div>

  <div v-else class="mx-auto w-full max-w-2xl px-4 py-6">
    <h1 class="text-xl font-semibold text-slate-900">购买套餐</h1>
    <p class="mt-1.5 text-sm leading-relaxed text-slate-600">
      额度按自然月重置；升级立即生效，只需补差价。本期内不支持降级，到期后可自由选择。
    </p>

    <!-- 待支付订单：弹窗被关掉（点了遮罩或 Esc）之后的回到支付入口 -->
    <section
      v-if="pendingOrder !== null && !orderPanelOpen"
      class="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-accent-200 bg-accent-50 p-4"
    >
      <div>
        <p class="text-sm font-medium text-accent-900">有一笔待支付订单</p>
        <p class="mt-0.5 text-xs text-accent-800">{{ orderAmountText }} · {{ countdownText }}</p>
      </div>
      <div class="flex gap-2">
        <Button size="sm" @click="orderPanelOpen = true">继续支付</Button>
        <Button size="sm" variant="ghost" :loading="closing" @click="cancelOrder">取消订单</Button>
      </div>
    </section>

    <div class="mt-5 flex gap-2" role="group" aria-label="计费周期">
      <button
        v-for="option in CYCLE_OPTIONS"
        :key="option.id"
        type="button"
        class="flex-1 rounded-xl border px-3 py-2 text-left text-sm transition-colors"
        :class="
          option.id === cycle
            ? 'border-brand-500 bg-brand-50 text-brand-700'
            : 'border-slate-200 bg-white text-slate-600 hover:border-brand-200'
        "
        :aria-pressed="option.id === cycle"
        @click="cycle = option.id"
      >
        <span class="font-medium">{{ option.label }}</span>
        <span class="ml-2 text-xs text-slate-500">{{ option.hint }}</span>
      </button>
    </div>

    <SkeletonText v-if="loading && !subscriptionReady" :lines="4" class="mt-4" />
    <section v-else class="mt-4 grid gap-3 sm:grid-cols-2">
      <button
        v-for="card in cards"
        :key="card.planId"
        type="button"
        class="rounded-xl border bg-white p-4 text-left transition-colors"
        :class="
          card.planId === selectedPlanId
            ? 'border-brand-500 ring-1 ring-brand-500'
            : 'border-slate-200 hover:border-brand-200'
        "
        :aria-pressed="card.planId === selectedPlanId"
        @click="selectedPlanId = card.planId"
      >
        <span class="flex items-baseline justify-between gap-2">
          <span class="text-sm font-semibold text-slate-900">{{ card.label }}</span>
          <span v-if="card.recommended" class="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
            推荐
          </span>
        </span>
        <span class="mt-2 block text-lg font-semibold text-slate-900">
          ¥{{ formatPriceYuan(card.amountFen) }}
          <span class="text-xs font-normal text-slate-500">/ {{ cycleUnit }}</span>
        </span>
        <!-- 划线价是这一页唯一表达"年付更便宜"的地方，额度那一行只说档位倍率 -->
        <span v-if="card.originalAmountFen !== null" class="mt-0.5 block text-xs text-slate-400">
          <span class="line-through">¥{{ formatPriceYuan(card.originalAmountFen ?? 0) }}</span>
          <span class="ml-1 text-accent-600">省 2 个月</span>
        </span>
        <!-- 额度只说相对倍率：绝对 token 量不下发到前端（v1.3.0）。 -->
        <span class="mt-2 block text-xs leading-relaxed text-slate-600">
          {{ card.quotaTierLabel }}
        </span>
      </button>
    </section>

    <section class="mt-4 rounded-xl border border-slate-200 bg-white p-4">
      <h2 class="text-sm font-semibold text-slate-900">支付方式</h2>
      <div class="mt-3 flex gap-2" role="group" aria-label="支付方式">
        <button
          v-for="option in CHANNEL_OPTIONS"
          :key="option.id"
          type="button"
          class="flex-1 rounded-lg border px-3 py-2 text-left text-sm transition-colors"
          :class="
            option.id === channel
              ? 'border-brand-500 bg-brand-50 text-brand-700'
              : 'border-slate-200 text-slate-600 hover:bg-slate-50'
          "
          :aria-pressed="option.id === channel"
          @click="channel = option.id"
        >
          <span class="font-medium">{{ option.label }}</span>
          <span class="mt-0.5 block text-xs text-slate-500">{{ option.hint }}</span>
        </button>
      </div>
    </section>

    <section class="mt-4 rounded-xl border border-slate-200 bg-white p-4">
      <h2 class="text-sm font-semibold text-slate-900">结算摘要</h2>
      <dl class="mt-3 space-y-2 text-sm">
        <div class="flex items-center justify-between gap-3">
          <dt class="text-slate-500">当前套餐</dt>
          <dd class="text-slate-800">{{ currentPlanText }}</dd>
        </div>
        <div class="flex items-center justify-between gap-3">
          <dt class="text-slate-500">购买类型</dt>
          <dd class="text-slate-800">{{ change === null ? '—' : change.headline }}</dd>
        </div>
        <div class="flex items-center justify-between gap-3">
          <dt class="text-slate-500">应付金额</dt>
          <dd class="text-base font-semibold text-slate-900">{{ amountText }}</dd>
        </div>
        <div class="flex items-center justify-between gap-3">
          <dt class="text-slate-500">权益生效区间</dt>
          <dd class="text-right text-slate-800">{{ grantRangeText }}</dd>
        </div>
        <div class="flex items-center justify-between gap-3">
          <dt class="text-slate-500">支付时限</dt>
          <dd class="text-slate-800">{{ pendingOrder === null ? payLimitText : countdownText }}</dd>
        </div>
      </dl>

      <p v-if="change !== null" class="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-600">
        {{ change.detail }}
      </p>
      <p
        v-else-if="refusal !== null"
        class="mt-3 rounded-lg border border-accent-200 bg-accent-50 px-3 py-2 text-xs leading-relaxed text-accent-800"
      >
        {{ refusal }}
      </p>
    </section>

    <p
      v-if="!subscriptionReady && !loading"
      class="mt-3 rounded-lg border border-accent-200 bg-accent-50 px-3 py-2 text-xs leading-relaxed text-accent-800"
    >
      订阅信息没有加载成功。为避免算错金额，下单已暂停。
      <button type="button" class="ml-1 font-medium text-accent-900 underline" @click="loadSubscription">
        重新加载
      </button>
    </p>

    <div class="mt-5">
      <Button size="lg" full-width :loading="creating" :disabled="!canPay" @click="startPayment">
        {{ payButtonText }}
      </Button>
      <p class="mt-2 text-center text-xs text-slate-500">{{ payWindowText }}</p>
    </div>
  </div>

  <!-- 页脚不在外壳里，每个页面自己渲染；`mt-auto` 把它压到底部 -->
  <AppFooter class="mt-auto" />

  <Modal
    :open="pendingOrder !== null && orderPanelOpen"
    title="扫码完成支付"
    :description="orderSummaryText"
    @close="orderPanelOpen = false"
  >
    <div v-if="pendingOrder !== null" class="space-y-3">
      <div v-if="qrImageSrc !== null" class="flex justify-center">
        <img
          :src="qrImageSrc"
          alt="支付二维码"
          class="h-44 w-44 rounded-lg border border-slate-200 bg-white object-contain"
        />
      </div>
      <div
        v-else
        class="rounded-lg border border-slate-200 bg-slate-50 px-3 py-4 text-center text-xs leading-relaxed text-slate-600"
      >
        <p>本次没有生成二维码图片。</p>
        <a
          v-if="qrFallback !== null"
          :href="qrFallback.href"
          target="_blank"
          rel="noopener noreferrer"
          class="mt-1 inline-block font-medium text-brand-700 hover:underline"
        >
          {{ qrFallback.label }}
        </a>
        <p v-else class="mt-1">请回到支付渠道完成付款。</p>
      </div>

      <p class="text-center text-xs text-slate-500">{{ countdownText }}</p>

      <Button full-width :loading="checking" @click="checkPayment">我已完成支付</Button>
      <!--
        模拟支付只对开发环境开放：Vite 在生产构建里把 `IS_DEV` 静态替换成 `false`
        （产物里是 `T2=!1`），所以这一支永远渲染不出来。真后端那边也受 `PAY_MOCK`
        开关约束，`PAY_MOCK=false` 时 `/payments/mock/*` 直接 40400——两道门都要有，
        因为"前端不显示按钮"从来不是一道安全边界。
      -->
      <Button v-if="IS_DEV" variant="secondary" full-width :loading="mockPaying" @click="mockPay">
        模拟支付成功（仅开发环境）
      </Button>
      <p class="text-center text-xs text-slate-400">订单号 {{ orderNoText }}</p>
    </div>

    <template #footer>
      <Button variant="ghost" :loading="closing" @click="cancelOrder">取消订单</Button>
      <Button variant="ghost" @click="orderPanelOpen = false">稍后再付</Button>
    </template>
  </Modal>
</template>
